import {
  Avatar,
  Box,
  IconButton,
  LinearProgress,
  Popover,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import { perseEventsWithZoom } from "./lib";
import { Event, IconMeetingMap, TConferenceType } from "./types";

import { useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { renderErrorToast } from "../../../../lib/ErrorToastMessage";
import MaterialModal from "../../../components/app/material-modal";
import MaterialInterviewDetails from "../../../components/discovery/interview/material-interview-details";
import { baseAtom } from "../../../stores/jotai/base";
import { zoomIntegrationStateAtom } from "../../../stores/jotai/calender&Zoom";
import { useGetGoogleEvents } from "../../../tanstackQuery/social-integrations/handle-google";
import {
  useGetEventByZoomUserUUID,
  useToggleAutoZoomMeetingProcessing
} from "../../../tanstackQuery/social-integrations/handle-zoom";
import {
  TMeetingResponse,
  TUserAuthStatusResponse
} from "../../../tanstackQuery/social-integrations/types";
import { ZoomDayEvent } from "./ZoomDayEvent";
import { XIcon } from "../../../components/app/notification-settings-panel";
import {
  Description,
  Launch,
  Phone,
  Square,
  Subject
} from "@mui/icons-material";
import IconNoEvent from "../../../components/icons/IconNoEvent";

export type ToggleEventPayload = {
  meetingId: string;
  meetingPassword: string;
  meetingLink: string;
  shouldBeRecorded: boolean;
  startTime: Date;
  appInterviewID: string;
  topic: string;
  conferenceType: TConferenceType;
  formattedTime: string;
  description: string;
  eventLink: string;
  phone: string;
  email:string[];
};

export type HandleToggleChangeProps = {
  meetingId: string;
  shouldBeRecorded: boolean;
  startTime: Date;
  appInterviewID?: string;
};

export function RenderMeetingInfo({ icon, children }) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={2}>
      {icon}
      {children}
    </Stack>
  );
}

const GoogleAndZoomLoggedIn = () => {
  const theme = useTheme();
  const [googleEvents, setGoogleEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const session = useAtomValue(discoverySessionAtom);
  const { keycloak } = useKeycloak();
  const client = useQueryClient();
  const [base, setBase] = useAtom(baseAtom);
  const [zoomIntegration, setZoomIntegration] = useAtom(
    zoomIntegrationStateAtom
  );
  const [openInterviewCreateModal, setOpenInterviewCreateModal] =
    useState<boolean>(false);

  const [toggleEventPayload, setToggleEventPayload] =
    useState<ToggleEventPayload>(null);

  const handleInterviewModalClose = () => {
    setOpenInterviewCreateModal(false);
  };

  const authStatus = client.getQueryData([
    "user-auth-status",
    { userId: base?.user?.userID, teamId: base?.team?.id }
  ]) as TUserAuthStatusResponse;

  const zoomMeetings = client.getQueryData([
    "zoom-meetings",
    { appTeamID: base?.team?.id }
  ]) as TMeetingResponse[];

  const { mutateAsync: toggleAutoZoomMeetingProcessing } =
    useToggleAutoZoomMeetingProcessing(
      { accessToken: keycloak.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["google-events"] });
          client.invalidateQueries({ queryKey: ["zoom-events"] });
          client.invalidateQueries({ queryKey: ["zoom-meetings"] });
        }
      }
    );

  const {
    data: fetchedEvents,
    isLoading: eventsLoading,
    isRefetching: eventsRefetching,
    refetch: eventsRefetch
  } = useGetGoogleEvents(
    { accessToken: keycloak.token },
    {
      teamId: base?.team?.id,
      userId: base?.user?.userID
    },
    {
      enabled: !!(base?.team?.id && base?.user?.userID)
      // refetchOnWindowFocus: false
    }
  );
  const { 
    data: fetchedEventsByZoomUserUUID,
    isLoading: eventsByIdLoading,
    isRefetching: eventsByIdRefetching,
    refetch: eventsByIdRefetch
  } = useGetEventByZoomUserUUID(
    { accessToken: keycloak.token },
    { hostUUID: authStatus?.zoomUserUUID },
    { enabled: !!authStatus?.zoomUserUUID, refetchOnWindowFocus: false }
  );

  const fetchGoogleEvents = async () => {
    setIsLoadingEvents(true);
    // For now stored in local storage, in future should be used or specific storage for that purpose or cookies with encoding with server
    try {
      const parsedEvents = perseEventsWithZoom(
        fetchedEvents,
        fetchedEventsByZoomUserUUID,
        zoomMeetings
      );
      setGoogleEvents(parsedEvents);
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  // Handle toggle change
  const handleToggleChange = async ({
    meetingId,
    shouldBeRecorded,
    startTime,
    appInterviewID
  }: HandleToggleChangeProps) => {
    if (!meetingId || !appInterviewID) return;

    try {
      const response = async () => {
        const response = await toggleAutoZoomMeetingProcessing({
          currentToggleState: shouldBeRecorded,
          queries: {
            meetingCode: meetingId,
            zoomUserUUID: authStatus?.zoomUserUUID
          },
          sessionID: session.sessionID,
          appTeamID: base.team.groupID,
          googleEventStartTime: startTime,
          appInterviewID
        });
        return response.statusText;
      };
      fetchGoogleEvents();

      toast.promise(response, {
        loading: shouldBeRecorded
          ? "Disabling Zoom meeting auto processing"
          : "Enabling Zoom meeting auto processing",
        // success: (data) => {
        //   return shouldBeRecorded
        //     ? `Zoom meeting auto processing disabled successfully`
        //     : `Zoom meeting auto processing enabled successfully`;
        // },
        error: "Something went wrong"
      });
    } catch (error) {
      renderErrorToast("Error updating the Zoom meeting:");
    }
  };

  const eventsGroupedByDay = googleEvents?.reduce<Record<string, Event[]>>(
    (eventsByDate, event) => {
      const dayKey = `${event.day} ${event.date}`;
      if (!eventsByDate[dayKey]) {
        eventsByDate[dayKey] = [];
      }
      eventsByDate[dayKey]?.push(event);
      return eventsByDate;
    },
    {}
  );
  useEffect(() => {
    async function refetchingEvents() {
      if (zoomIntegration.eventsRefetching) {
        await eventsRefetch();
        await eventsByIdRefetch();
        setZoomIntegration((prev) => ({
          ...prev,
          eventsRefetching: false,
          eventsRefetchingWithPubnub: false
        }));
      }
    }

    refetchingEvents();
  }, [
    zoomIntegration.eventsRefetching,
    zoomIntegration.eventsRefetchingWithPubnub
  ]);

  useEffect(() => {
    if (fetchedEvents?.length) fetchGoogleEvents();
  }, [
    fetchedEvents,
    fetchedEventsByZoomUserUUID,
    zoomIntegration.eventsRefetching,
    zoomIntegration.eventsRefetchingWithPubnub
  ]);

  const IconMeeting =
    IconMeetingMap[toggleEventPayload?.conferenceType] || null;

  const [anchorEl, setAnchorEl] = useState<
    HTMLDivElement | HTMLButtonElement | null
  >(null);

  const handleOpenMeetingDetailsModal = (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const isAllEventsLoading =
    isLoadingEvents ||
    eventsLoading ||
    eventsByIdLoading ||
    (zoomIntegration.eventsRefetching &&
      !zoomIntegration.eventsRefetchingWithPubnub &&
      (eventsRefetching || eventsByIdRefetching));
  return (
    <>
      <MaterialModal
        title={`Create Interview for meeting - ${toggleEventPayload?.topic}`}
        open={openInterviewCreateModal}
        onClose={handleInterviewModalClose}
        handleDialogClose={handleInterviewModalClose}
        styleOverrides={{
          container: {
            "& .MuiDialog-paper": {
              width: "100%",
              minHeight: "500px",
              minWidth: "780px",
            },
          },
        }}
      >
        <MaterialInterviewDetails
          zoomToggleEventPayload={toggleEventPayload}
          handleToggleZoomEvent={toggleEventPayload ? handleToggleChange : null}
          style={{ width: "812px" }}
          interview={{}}
          import={undefined}
          existingInterview={false}
          audioImportDoneUploading={true}
          close={handleInterviewModalClose}
          errors={{}}
          handleErrors={() => {}}
          setBase={setBase}
        />
      </MaterialModal>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
      >
        <Box sx={{ width: 500 }} p={2} pl={3}>
          <Stack direction="row" justifyContent="flex-end">
            <IconButton color="inherit" onClick={handleClose}>
              <XIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack spacing={2}>
            <RenderMeetingInfo
              icon={
                <Square
                  fontSize="small"
                  sx={{ position: "relative", top: "5px" }}
                />
              }
            >
              <Box>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography fontSize={18}>
                    {toggleEventPayload?.topic}
                  </Typography>
                  <Tooltip
                    title="Redirect to calendar"
                    PopperProps={{
                      modifiers: [
                        {
                          name: "zIndex",
                          enabled: true,
                          phase: "write",
                          fn: ({ state }) => {
                            state.styles.popper.zIndex = 1500;
                          },
                        },
                      ],
                    }}
                  >
                    <Box
                      component="a"
                      href={toggleEventPayload?.eventLink}
                      target="_blank"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "#828282",
                        "&:hover": {
                          color: "#1976D2",
                        },
                      }}
                    >
                      <Launch fontSize="small" />
                    </Box>
                  </Tooltip>
                </Stack>

                <Typography fontSize={15}>
                  {toggleEventPayload?.formattedTime}
                </Typography>
              </Box>
            </RenderMeetingInfo>

            <RenderMeetingInfo
              icon={
                IconMeeting ? (
                  <IconMeeting
                    width={18}
                    height={18}
                    style={{ position: "relative", top: "5px" }}
                  />
                ) : null
              }
            >
              <Box>
                {toggleEventPayload?.conferenceType === "External Meeting" ? (
                  <Typography
                    fontSize={15}
                    component="a"
                    target="_blank"
                    color="#1976D2"
                    href={toggleEventPayload?.meetingLink}
                  >
                    {toggleEventPayload?.meetingLink}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      fontSize={15}
                      component="a"
                      target="_blank"
                      color="#1976D2"
                      href={toggleEventPayload?.meetingLink}
                    >
                      Join {toggleEventPayload?.conferenceType}
                    </Typography>
                    <Typography fontSize={15}>
                      Meeting Id: {toggleEventPayload?.meetingId}
                    </Typography>
                    {toggleEventPayload?.conferenceType === "Zoom Meeting" && (
                      <Typography fontSize={15}>
                        Passcode: {toggleEventPayload?.meetingPassword}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </RenderMeetingInfo>

            {toggleEventPayload?.description && (
              <RenderMeetingInfo
                icon={
                  <Subject
                    fontSize="small"
                    sx={{ position: "relative", top: "5px" }}
                  />
                }
              >
                <Box
                  sx={{ fontSize: "15px" }}
                  dangerouslySetInnerHTML={{
                    __html: toggleEventPayload?.description,
                  }}
                />
              </RenderMeetingInfo>
            )}
          </Stack>
        </Box>
      </Popover>

      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          bgcolor: "white",
          pt: 1,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        {isAllEventsLoading ? (
          <LinearProgress />
        ) : (
          <Stack
            direction="column"
            sx={{
              width: "100%",
              mt: 1,
              flexGrow: 1,    
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {Object.entries(eventsGroupedByDay || {}).length > 0 ? (
              Object.entries(eventsGroupedByDay || {}).map(
                ([dayDate, dayEvents]) => {
                  return (
                    <Box key={dayDate}>
                      <Stack
                        direction="row"
                        sx={{
                          width: "100%",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          mb: 1,
                        }}
                        px={2}
                      >
                        <Avatar
                          sx={{
                            width: 35,
                            height: 35,
                            bgcolor: dayEvents?.[0]?.isToday
                              ? theme.custom.mainButtonHighlight
                              : theme.custom.gray6,
                            marginRight: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: "600",
                              color: dayEvents?.[0]?.isToday
                                ? theme.custom.white
                                : "#595959",
                              borderRadius: "500px",
                              padding: "12px",
                            }}
                          >
                            {dayEvents?.[0]?.date}
                          </Typography>
                        </Avatar>
                        <Typography
                          sx={{
                            fontWeight: "600",
                            color: dayEvents[0]?.isToday
                              ? theme.custom.mainButtonHighlight
                              : "#595959",
                          }}
                        >{`${dayEvents?.[0]?.month}, ${dayEvents?.[0]?.day}`}</Typography>
                      </Stack>
                      <Stack spacing={2} mb={1}>
                        {dayEvents?.map((event) => {
                          return (
                            <ZoomDayEvent
                              event={event}
                              zoomUserUUID={authStatus?.zoomUserUUID}
                              handlers={{
                                setOpenInterviewCreateModal,
                                handleToggleChange,
                                setToggleEventPayload,
                                handleOpenMeetingDetailsModal,
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                }
              )
            ) : (
          <Stack direction={"column"} sx={{
              flex: 1,                
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              }}>
              <IconNoEvent />
              <Typography mt={2} sx={{
                fontSize:"15px",
                color:theme.palette.primary.main,
                fontWeight:theme.typography.fontWeightBold
              }}>
                No Event Found!
              </Typography>
          </Stack>
            )}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default GoogleAndZoomLoggedIn;
